import React from 'react';
import {trim} from 'lodash';
import {toStringId} from '@packages/systems/core/utils/HtmlUtils';
import {
  Expressions,
  withSystem,
  withMeta,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  updateExpressionsAction,
  updateExpressionAtAction,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TRecord,
  TText,
  TTypeConstructor,
  TEnum,
  EText,
} from '@packages/systems/wfdl-ast';
import {
  ATTR_REQUIRED_PATH,
  ATTR_NAME_PATH,
  ATTR_DATA_NAME_PATH,
  ATTR_ID_PATH,
  ATTR_ACCEPT_PATH,
  FORM_NAME_PATH,
  FORM_MANAGE_PATH,
  FORM_DOCUMENTS_PATH,
  FORM_IMAGES_PATH,
  FORM_CUSTOM_PATH,
  FORM_CUSTOM_TYPES_PATH,
  FILE_DEFAULT_LABEL_CAP,
  FILE_DEFAULT_LABEL,
} from '../constants';
import {TEMP_STATE_PATH} from '@packages/utilities/expression-path-constants';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {getNewElementId} from '@packages/utilities/platform';
import {buildAcceptString} from '../FormFileUploadUtils';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const {getIn} = ContextualExpressions;

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: tokens.space[2],
  marginBottom: tokens.space[2],
});
const IndentedGrid = emotionStyled('div')({
  display: 'grid',
  paddingLeft: tokens.space[5],
});
const InfoMessage = emotionStyled('div')({
  marginTop: tokens.space[2],
  color: tokens.colors.textPrimary,
});
const LinkMessage = emotionStyled('a')({
  color: tokens.colors.textPrimary,
});

const Divider = emotionStyled('div')({
  flexGrow: 1,
  height: 1,
  backgroundColor: tokens.colors.border1,
  marginBottom: tokens.space[1],
});
// @ts-expect-error - TS7031 - Binding element 'DesignerStore' implicitly has an 'any' type.
const selectComponents = ({DesignerStore}) =>
  DesignerStore.stagedComponents || DesignerStore.components;

const fields = {
  temp: TRecord({
    state: withSystem(
      {
        defaultValue: 'default',
        label: 'State',
        isFullWidth: true,
        isLocalizable: true,
      },
      TEnum({
        default: 'Default',
        uploading: 'Uploading',
        success: 'Success',
        error: 'Error',
      })
    ),
  }),
  form: TRecord({
    type: withSystem({defaultValue: 'file-upload-input', private: true}, TText),
    name: withMeta(
      {placeholder: 'File upload'},
      withSystem(
        {
          label: 'Name',
          defaultValue: FILE_DEFAULT_LABEL_CAP,
          isLocalizable: true,
        },
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
      )
    ),
    manageTypes: withSystem(
      {
        labelVariant: 'hidden',
        label: 'Allow only specific file types',
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    acceptDocuments: withSystem(
      {
        labelVariant: 'hidden',
        label: 'Document .doc, .pdf, .txt…',
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    acceptImages: withSystem(
      {
        labelVariant: 'hidden',
        label: 'Image .jpg, .gif, .png…',
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    acceptCustom: withSystem(
      {labelVariant: 'hidden', label: 'Custom', isLocalizable: true},
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    customTypes: withMeta(
      {placeholder: 'Format: .psd, .pdf, .doc, .png'},
      withSystem(
        {isLocalizable: true},
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
      )
    ),
  }),
  attr: TRecord({
    id: withSystem(
      {
        private: true,
        defaultValue: FILE_DEFAULT_LABEL,
        label: 'ID',
        isBindable: false,
        isLocalizable: true,
      },
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('IdTextInput')))
    ),
    required: withSystem(
      {labelVariant: 'hidden', label: 'Required', isLocalizable: true},
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    multiple: withSystem(
      {labelVariant: 'hidden', label: 'Multiple', isLocalizable: true},
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    accept: withSystem(
      {private: true, defaultValue: '', isLocalizable: true},
      TText
    ),
    type: withSystem({defaultValue: FILE_DEFAULT_LABEL, private: true}, TText),
    name: withSystem(
      {defaultValue: FILE_DEFAULT_LABEL, private: true, isLocalizable: true},
      TText
    ),
    'data-name': withSystem(
      {
        defaultValue: FILE_DEFAULT_LABEL_CAP,
        private: true,
        isLocalizable: true,
      },
      TText
    ),
    'aria-hidden': withSystem({defaultValue: 'true', private: true}, TText),
    tabIndex: withSystem({defaultValue: '-1', private: true}, TText),
  }),
} as const;

const FormFileUploadType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: function RenderFormFileUploadType({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: (arg1?: any) => void;
  }) {
    const componentMap = useDesignerSelector(selectComponents);
    const onChangeAt = React.useCallback(
      (at: ExpressionPath) => extendExpressionEditorActionAtPath(dispatch)(at),
      [dispatch]
    );
    const onNameCommit = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'dispatchExpr' implicitly has an 'any' type.
      (dispatchExpr) => {
        const currentNameFromExpr = getIn(expr, FORM_NAME_PATH);
        const currentName = currentNameFromExpr
          ? ContextualExpressions.getText(stripAnn(currentNameFromExpr))
          : FILE_DEFAULT_LABEL_CAP;
        const newName =
          Expressions.getText(dispatchExpr?.payload?.newExpression) || '';
        const trimmedName = trim(newName);
        const idString = toStringId(trimmedName) || '';
        const trimmedNameExp = EText(trim(newName));
        const changes = [
          {key: FORM_NAME_PATH, newExpression: trimmedNameExp},
          {key: ATTR_DATA_NAME_PATH, newExpression: trimmedNameExp},
        ];
        const idNeedsUpdate =
          currentName && idString !== toStringId(currentName);
        if (idNeedsUpdate) {
          const newId = getNewElementId(
            componentMap,
            idString || FILE_DEFAULT_LABEL_CAP
          );
          const newIdExp = EText(newId);
          changes.push(
            {key: ATTR_NAME_PATH, newExpression: newIdExp},
            {key: ATTR_ID_PATH, newExpression: newIdExp}
          );
        }
        dispatch(updateExpressionsAction(changes));
      },
      [expr, dispatch, componentMap]
    );
    const expressionEditorAtPath = React.useCallback(
      (path: ExpressionPath) => {
        const exprAt = getIn(expr, path);
        const onDispatch =
          path === FORM_NAME_PATH ? onNameCommit : onChangeAt(path);
        if (!exprAt) {
          throw Error(
            `Failed to get expression at path: ${JSON.stringify(path)}`
          );
        }
        return {
          exprAt: stripAnn(exprAt),
          component: (
            <ExpressionEditor expression={exprAt} dispatch={onDispatch} />
          ),
        };
      },
      [expr, onChangeAt, onNameCommit]
    );

    const {component: State} = expressionEditorAtPath(TEMP_STATE_PATH);
    const {component: Name} = expressionEditorAtPath(FORM_NAME_PATH);
    const {component: Required} = expressionEditorAtPath(ATTR_REQUIRED_PATH);
    const {exprAt: manageTypesExp, component: ManageTypes} =
      expressionEditorAtPath(FORM_MANAGE_PATH);
    const {exprAt: documentsExp, component: Documents} =
      expressionEditorAtPath(FORM_DOCUMENTS_PATH);
    const {exprAt: imagesExp, component: Images} =
      expressionEditorAtPath(FORM_IMAGES_PATH);
    const {exprAt: customExp, component: Custom} =
      expressionEditorAtPath(FORM_CUSTOM_PATH);
    const {exprAt: customTypesExp, component: CustomTypes} =
      expressionEditorAtPath(FORM_CUSTOM_TYPES_PATH);

    const manageTypes = ContextualExpressions.getBoolean(manageTypesExp);
    const acceptDocuments = ContextualExpressions.getBoolean(documentsExp);
    const acceptImages = ContextualExpressions.getBoolean(imagesExp);
    const acceptCustom = ContextualExpressions.getBoolean(customExp);
    const customTypes = ContextualExpressions.getText(customTypesExp);
    const {exprAt: acceptExpr} = expressionEditorAtPath(ATTR_ACCEPT_PATH);
    const [accept, setAccept] = React.useState(
      ContextualExpressions.getText(acceptExpr) || ''
    );
    React.useEffect(() => {
      const newAccept = !manageTypes
        ? ''
        : buildAcceptString({
            // @ts-expect-error - Flow 0.121.0
            acceptDocuments,
            // @ts-expect-error - Flow 0.121.0
            acceptImages,
            // @ts-expect-error - Flow 0.121.0
            acceptCustom,
            // @ts-expect-error - Flow 0.121.0
            customTypes,
          });
      if (newAccept !== accept) {
        setAccept(newAccept);
        dispatch(updateExpressionAtAction(EText(newAccept), ATTR_ACCEPT_PATH));
      }
    }, [
      acceptDocuments,
      acceptImages,
      acceptCustom,
      customTypes,
      accept,
      dispatch,
      manageTypes,
    ]);

    return (
      <>
        <Grid>
          {State}
          {Name}
        </Grid>
        <Divider />
        {Required}
        {ManageTypes}
        {manageTypes ? (
          <IndentedGrid>
            {Documents}
            {Images}
            {Custom}
            {acceptCustom ? (
              <>
                {CustomTypes}
                <InfoMessage>
                  Executable file types are not allowed for security
                  reasons.&nbsp;
                  <LinkMessage
                    href="https://help.webflow.com/hc/en-us/articles/33961307228947-Form-file-upload#supported-file-types-for-file-upload"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read more
                  </LinkMessage>
                </InfoMessage>
              </>
            ) : null}
          </IndentedGrid>
        ) : null}
      </>
    );
  },
});

export default FormFileUploadType;
